/**
 * Page Registry Configuration
 *
 * Central registry of all pages in the application with their permission requirements.
 * To add a new page to the permission system:
 * 1. Add entry to PAGES array below
 * 2. Add corresponding key to PageAccessConfig in permissions.types.ts
 * 3. Use PageAccessGuard component in the page
 */

import type { PageConfig } from '../types/permissions.types';

/**
 * All pages in the application
 */
export const PAGES: PageConfig[] = [
  // Location-based pages (have location selector + editor/viewer roles)
  {
    id: 'rentals',
    displayName: 'Rental Equipment Tracker',
    path: '/rentals',
    hasLocations: true,
    requiresAuth: true,
    description: 'Multi-location rental equipment status tracking',
    department: 'rental',
    permissionHelp: {
      viewer:
        'View the board, reservation details and cleared-reservation history; search and filter.',
      editor:
        'Create and edit reservations (including print, swap and unlink), copy/paste cells, set item flags and tags, update service dates, use Action Items and Fuel, and use edit mode (desktop) to add, rename and reorder rows.',
      notes: ['The Fuel button also needs Fuel Calculator access.'],
    },
  },
  {
    id: 'vendors',
    displayName: 'Vendor Management',
    path: '/vendors',
    hasLocations: true,
    requiresAuth: true,
    description: 'Vendor login credential tracking and management',
    department: 'parts',
    permissionHelp: {
      viewer:
        "View and search the location's vendor logins, including revealing and copying passwords.",
      editor:
        'Add, edit, delete and reorder vendors.',
    },
  },
  {
    id: 'backorders',
    displayName: 'Backorders',
    path: '/parts/backorders',
    hasLocations: true,
    requiresAuth: true,
    description: 'Track ticket-linked backordered parts and missed shipments per location',
    department: 'parts',
    permissionHelp: {
      viewer: "View the location's backorders, flags, activity and history.",
      editor: 'Add lines, set ETAs and shipments, add notes, split, cancel and receive lines.',
      notes: ['Flag thresholds are admin-only (settings button on the page).'],
    },
  },
  {
    id: 'label-printer',
    displayName: 'Label Printer',
    path: '/label_printer',
    hasLocations: true,
    requiresAuth: true,
    description: 'Search and print Zebra ZPL labels for parts',
    department: 'parts',
    permissionHelp: {
      viewer:
        'Search parts (by part number, bin, description or bin range), build custom labels and print them.',
      editor:
        "Upload the location's parts data CSV (desktop only).",
    },
  },
  {
    id: 'stamps',
    displayName: 'Concrete Stamp Rentals',
    path: '/supplies/stamps',
    hasLocations: true,
    requiresAuth: true,
    description: 'Multi-location concrete stamp rental tracking',
    department: 'supplies',
    permissionHelp: {
      viewer:
        'View the board, reservation details and cleared-reservation history; search.',
      editor:
        'Create, edit, clear and copy/paste reservations, and use edit mode (desktop) to add, rename and reorder items.',
    },
  },
  {
    id: 'billOfLading',
    displayName: 'BOL Generator',
    path: '/parts/bill-of-lading',
    hasLocations: true,
    requiresAuth: true,
    description: 'Generate, preview, print, and track Bills of Lading for freight shipments',
    department: 'parts',
    isNew: true,
    permissionHelp: {
      viewer:
        'Browse and search BOL history and reprint past BOLs (to a printer or as a PDF). The new-BOL form is read-only.',
      editor:
        "Create and generate BOLs, edit BOL numbers, and manage the location's address and carrier books.",
    },
  },
  {
    id: 'truckingCompanies',
    displayName: 'Trucking Companies',
    path: '/rental/trucking-companies',
    hasLocations: true,
    requiresAuth: true,
    description: 'Manage trucking company contact information',
    department: 'rental',
    permissionHelp: {
      viewer:
        "View and search the location's trucking company contacts.",
      editor:
        'Add, edit, delete and reorder trucking companies.',
      notes: [
        'The trucking company pickers on the Trucking Calendar and Rental Board read this list, so anyone scheduling trucking needs at least Viewer here.',
      ],
    },
  },
  {
    id: 'truckingCalendar',
    displayName: 'Trucking Calendar',
    path: '/rental/trucking-calendar',
    hasLocations: true,
    requiresAuth: true,
    description: 'Week view calendar of scheduled deliveries and pickups',
    department: 'rental',
    permissionHelp: {
      viewer:
        'View the calendar, open deliveries and entries, change delivery status (scheduled, picked up, delivered), print or save PDFs and draft emails.',
      editor:
        'Add calendar entries; edit dates, times, trucking company, address and notes; duplicate and delete entries.',
      notes: [
        'Deleting archived entries needs Rental Board Editor at the location.',
      ],
    },
  },
  {
    id: 'yard',
    displayName: 'Yard Dashboard',
    path: '/yard',
    hasLocations: true,
    requiresAuth: true,
    description: 'Mobile-focused equipment prep and return management',
    department: 'rental',
    permissionHelp: {
      viewer:
        'Do the yard work: start and complete preps and returns (photos, hours), including cross-location returns. Sends prep/return notifications.',
      editor:
        'Upload photos directly to the Photo Archive and swap equipment on prep tasks.',
      notes: [
        'Viewer is the normal level for yard staff.',
        'Which cross-location returns are offered is set in Admin → Cross-Location, not by this role.',
      ],
    },
  },
  {
    id: 'archive',
    displayName: 'Photo Archive',
    path: '/rental/archive',
    hasLocations: true,
    requiresAuth: true,
    description: 'Historical record of rental equipment photos and conditions',
    department: 'rental',
    permissionHelp: {
      viewer:
        'Search by equipment ID or date and browse prep, return and upload photo records.',
      editor:
        'Upload photos manually and delete records.',
    },
  },
  // Role-based pages (editor/viewer but no locations)
  {
    id: 'lostRevenue',
    displayName: 'Lost Revenue Board',
    path: '/rental/lost-revenue',
    hasLocations: false,
    supportsRoles: true,
    isDivisionScoped: true,
    requiresAuth: true,
    description: 'Track missed rental requests to identify fleet gaps',
    department: 'rental',
    permissionHelp: {
      viewer:
        'View the equipment board, recent requests and analytics; search and filter.',
      editor:
        'Log new items and +1 requests, and edit or delete their own entries.',
      notes: [
        "Rates, item status, merging, renaming, deleting items and editing others' entries are admin-only.",
        'New Item only offers locations where the user is a Rental Board Editor.',
      ],
    },
  },
  {
    id: 'fuel-calculator',
    displayName: 'Fuel Calculator',
    path: '/fuel-calculator',
    hasLocations: false,
    supportsRoles: true,
    requiresAuth: true,
    description: 'Calculate fuel billing for returned rental equipment (shared across all locations)',
    department: 'rental',
    permissionHelp: {
      viewer:
        'Use the calculator: pick a machine and fuel level to get gallons to bill.',
      editor:
        'Add, edit and delete machines and their fuel capacities.',
    },
  },
  {
    id: 'directory',
    displayName: 'Phone Directory',
    path: '/parts/directory', // Primary path (also available at /rental/directory, /supplies/directory)
    hasLocations: false,
    supportsRoles: true,
    isDivisionScoped: true,
    requiresAuth: true,
    description: 'Company-wide phone directory (shared across all departments)',
    department: 'shared',
    permissionHelp: {
      viewer:
        'Browse and search contacts by category.',
      editor:
        'Add, edit, delete and reorder categories, their fields and contacts.',
      notes: [
        'Data is per division; Editor applies to every division the user can switch to.',
      ],
    },
  },
  {
    id: 'quizzes',
    displayName: 'Quizzes',
    path: '/quizzes', // Primary path (also available at /{department}/quizzes)
    hasLocations: true,
    // Quiz managers (an admin-set switch, not a grant) build and assign at every store;
    // a store Viewer grant only keeps My Quizzes in the sidebar. docs/archive/QUIZ_SCENARIOS_PLAN.md § 0
    managerFlag: 'quizManager',
    editorsOnly: true,
    requiresAuth: true,
    description: "Store managers: this store's quiz results and scenario runs, and reviewing scenario steps",
    department: 'shared',
    permissionHelp: {
      viewer:
        'Keeps My Quizzes in the sidebar even with nothing assigned, so they can look back at their own results. Does not open the Quizzes page.',
      editor:
        "Opens the Quizzes page for this store: each person's quiz status, score % and pass/fail (never their answers or the answer keys), this store's scenario runs in full, Verify, Redo and reassign on their steps, and changing a run's reviewer. Can be picked as a scenario run's reviewer.",
      notes: [
        'Building and assigning quizzes and scenarios, answer keys and full results need the Quiz manager switch (Admin → Quiz managers), not this permission.',
        'Surveys show completion only.',
        'Taking an assigned quiz or answering a scenario step needs no access to this page.',
        'Grants from before Quizzes was per store no longer work: re-grant per store.',
      ],
    },
  },

  // Admin pages (admin-only access)
  {
    id: 'admin',
    displayName: 'Admin Panel',
    path: '/admin',
    hasLocations: false,
    requiresAuth: true,
    adminOnly: true,
    description: 'System administration and user management',
  },

  // Role-based reference pages (editor/viewer but no locations)
  {
    id: 'bobcatBook',
    displayName: 'Parts Reference Book',
    path: '/parts/references',
    hasLocations: false,
    supportsRoles: true,
    isDivisionScoped: true,
    requiresAuth: true,
    description: 'Parts reference PDFs and documentation',
    department: 'parts',
    permissionHelp: {
      viewer:
        'Browse categories, search and open documents.',
      editor:
        'Use edit mode to add, rename, delete and reorder categories, and upload, replace, edit, delete and reorder documents.',
      notes: [
        'Data is per division; Editor applies to every division the user can switch to.',
      ],
    },
  },
  {
    id: 'suppliesBook',
    displayName: 'Supplies Reference Book',
    path: '/supplies/references',
    hasLocations: false,
    supportsRoles: true,
    isDivisionScoped: true,
    requiresAuth: true,
    description: 'Supplies reference PDFs and documentation',
    department: 'supplies',
    permissionHelp: {
      viewer:
        'Browse categories, search and open documents.',
      editor:
        'Use edit mode to add, rename, delete and reorder categories, and upload, replace, edit, delete and reorder documents.',
      notes: [
        'Data is per division; Editor applies to every division the user can switch to.',
      ],
    },
  },
  {
    id: 'rentalReferences',
    displayName: 'Rental References',
    path: '/rental/references',
    hasLocations: false,
    supportsRoles: true,
    isDivisionScoped: true,
    requiresAuth: true,
    description: 'Rental reference documents and resources',
    department: 'rental',
    permissionHelp: {
      viewer:
        'Browse categories, search and open documents.',
      editor:
        'Use edit mode to add, rename, delete and reorder categories, and upload, replace, edit, delete and reorder documents.',
      notes: [
        'Data is per division; Editor applies to every division the user can switch to.',
      ],
    },
  },

  // Simple pages (boolean access only, no locations)
  {
    id: 'tracks',
    displayName: 'Track Information',
    path: '/tracks',
    hasLocations: false,
    requiresAuth: true,
    description: 'Track specifications and information',
    department: 'parts',
    permissionHelp: {
      access:
        'Search track cross-reference data by make, model or size. Read-only.',
    },
  },
  {
    id: 'bolt-decoder',
    displayName: 'Bolt Part Number Decoder',
    path: '/parts/bolt-decoder',
    hasLocations: false,
    requiresAuth: true,
    description: 'Decode and encode bolt part numbers with head code reference',
    department: 'parts',
    permissionHelp: {
      access:
        'Decode and encode bolt part numbers and browse the head-code reference. Read-only.',
    },
  },
  {
    id: 'equipmentSearch',
    displayName: 'Equipment Search',
    path: '/rental/equipment-search',
    hasLocations: false,
    isDivisionScoped: true,
    requiresAuth: true,
    description: 'Search for equipment across all locations you have rental board access to',
    department: 'rental',
    permissionHelp: {
      access:
        'Search equipment across every Rental Board location the user has (Viewer or Editor). Read-only.',
      notes: [
        'Finds nothing without Rental Board access.',
      ],
    },
  },

  // Public pages (no authentication required)
  {
    id: 'home',
    displayName: 'Home',
    path: '/',
    hasLocations: false,
    requiresAuth: false,
    description: 'Department landing page',
  },
  {
    id: 'quizInbox',
    displayName: 'My Quizzes',
    path: '/quiz',
    hasLocations: false,
    requiresAuth: false, // Access is per-assignment (enforced by Firestore rules), like home
    description: 'Quizzes and scenario steps assigned to the current user',
  },
  {
    id: 'login',
    displayName: 'Login',
    path: '/login',
    hasLocations: false,
    requiresAuth: false,
    description: 'Authentication page',
  },
];

/**
 * Location configuration
 * Re-exported from firebase config for backward compatibility
 *
 * Note: This creates a Record<string, string> map for quick lookups,
 * extracting just the short name (e.g., "Henderson" instead of "11 - Henderson")
 */
import { getLocationIds, getLocationShortName } from '../store/locationsStore';


// Export as PAGE_LOCATIONS for backward compatibility (computed on access)
// Note: This is a getter that returns current state - not reactive in React
export const PAGE_LOCATIONS: Record<string, string> = new Proxy({} as Record<string, string>, {
  get(_, prop: string) {
    return getLocationShortName(prop);
  },
  ownKeys() {
    return getLocationIds();
  },
  getOwnPropertyDescriptor() {
    return { enumerable: true, configurable: true };
  },
});

/**
 * Get all location IDs (dynamically from locationsStore)
 */
export function getAllLocationIds(): string[] {
  return getLocationIds();
}

/**
 * Get location display name (short version without ID prefix)
 */
export function getPageLocationName(locationId: string): string {
  return getLocationShortName(locationId);
}

/**
 * Find page config by ID
 */
export function getPageConfig(pageId: string): PageConfig | undefined {
  return PAGES.find((page) => page.id === pageId);
}


/**
 * Get all pages that require authentication
 */
export function getAuthenticatedPages(): PageConfig[] {
  return PAGES.filter((page) => page.requiresAuth);
}



